import React, { createContext, useContext, useState, useEffect } from "react";
import authAPI from "@/lib/api/auth.js";
import { api } from "../api/api";

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userCompany, setUserCompany] = useState(null);
  const [loadingUserCompany, setLoadingUserCompany] = useState(true);

  // --- THEME LOGIC ---
  const [theme, setTheme] = useState(() => {
    // 1. Check local storage
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) return savedTheme;

    // 2. Check system preference if no saved theme
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  });

  // Apply theme to the HTML element whenever it changes
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    
    // Save to localStorage
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };


  // Check for existing token and validate it on app start
  useEffect(() => {
    const init = async () => {
      try {
        await refreshUser();
      } catch (error) {
        console.log(error);
      }

      setIsLoading(false);
    };

    init();
  }, []);

  const login = async (userCredentials) => {
    setIsLoading(true);
    const response = await authAPI.signIn(userCredentials);
    if (response.ok) await refreshUser();
    setIsLoading(false);
    return response;
  };

  const signup = async (userData) => {
    setIsLoading(true);
    const response = await authAPI.signUp(userData);
    if (response.ok) window.location.reload();
    setIsLoading(false);
    return response;
  };

  const logout = async () => {
    try {
      await authAPI.signOut();
    } catch (error) {
      console.error("Logout failed:", error);
    } finally {
      setUser(null);
      // Tokens are already cleared in authAPI.signOut()
    }
  };

  const refreshUser = async () => {
    try {
      const user = await authAPI.getUserInfo();
      setUser(user.data.user);
    } catch (error) {
      console.error("Failed to refresh user:", error);
      // If we can't get user data, they might not be authenticated
      setUser(null);
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
    }
  };

  const getUserCompany = async () => {
    setLoadingUserCompany(true);
    
    const response = await api.getUserCompany();

    let loadedCompany;
    switch (response.status) {
      case 404: // user hasn't added a company
        setUserCompany(null);
        break;
      case 200:
        loadedCompany = await response.json();
        setUserCompany(loadedCompany);
        break;
      default:
        console.log("Unable to get user company");
        break;
    }

    setLoadingUserCompany(false);
  };

  useEffect(() => {
    if (user) getUserCompany();
  }, [user]);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    signup,
    logout,
    refreshUser,
    userCompany,
    loadingUserCompany,
    theme,
    toggleTheme,
    isDark: theme === "dark",
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};
